/**
 * Konfiguration der erweiterten Auswahl (Preline `advanced-select`, Musterkatalog M-002 F6/F7/F8).
 * Reine Funktionen ohne React und ohne DOM, damit die Konfiguration testbar bleibt. Alle Klassenketten sind fest
 * und vollständig (nie zusammengesetzt, Plan Regel 8); Texte aus Daten werden für das Markup in den Vorlagen
 * HTML-maskiert (`esc`). Avatare: das Bild des Profils (eigene Quelle), sonst Initialen – nie Fremdbilder.
 */
export type HsModus = "einzeln" | "mehrfach" | "tags";
export type FeldGroesseHs = "standard" | "kompakt" | "ruhig";

/** Vier feste Farb-Tokens der Tags (M-002 F8, Abweichung AW-006). Rot bleibt Risiko-Zuständen vorbehalten (M-001). */
export type TagFarbe = "primaer" | "gruen" | "orange" | "grau";

export interface HsOption {
  wert: string;
  label: string;
  /** Zweite Zeile (Rolle, Beschreibung). */
  beschreibung?: string;
  /** Fertiges, bereits maskiertes Markup des Symbols (Avatar, Farbpunkt). */
  symbolHtml?: string;
  deaktiviert?: boolean;
  /** Gruppenname: Optionen mit gleichem Namen erscheinen unter einer Überschrift. */
  gruppe?: string;
}

/** Farbpunkt je Tag-Farbe (Abweichung AW-006: Farbe als Marker, die Chips selbst bleiben neutral). */
export const TAG_PUNKT: Record<TagFarbe, string> = {
  primaer: "inline-block size-2.5 rounded-full bg-primary",
  gruen: "inline-block size-2.5 rounded-full bg-status-ok",
  orange: "inline-block size-2.5 rounded-full bg-status-warn",
  grau: "inline-block size-2.5 rounded-full bg-muted-foreground-1",
};

export function esc(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/** Initialen aus einem Namen: erster Buchstabe des ersten und des letzten Wortes; Klammerzusätze wie „(Extern)“ zählen nicht mit. */
export function initialen(name: string): string {
  const teile = name.replace(/\([^)]*\)|\[[^\]]*\]/g, " ").trim().split(/\s+/).filter(Boolean);
  if (teile.length === 0) return "?";
  const erster = teile[0]![0]!;
  const letzter = teile.length > 1 ? teile[teile.length - 1]![0]! : "";
  return (erster + letzter).toLocaleUpperCase("de-DE");
}

export const AVATAR_BILD = "shrink-0 size-6 rounded-full";
export const AVATAR_INITIALEN = "inline-flex shrink-0 items-center justify-center size-6 rounded-full bg-surface text-xs font-medium text-surface-foreground";

/** Avatar einer Person als Markup: Profilbild (nur eigene Quelle, relativer Pfad) oder Initialen. */
export function personSymbolHtml(name: string, avatarUrl?: string | null): string {
  if (avatarUrl && avatarUrl.startsWith("/")) {
    return `<img class="${AVATAR_BILD}" src="${esc(avatarUrl)}" alt="" />`;
  }
  return `<span class="${AVATAR_INITIALEN}" aria-hidden="true">${esc(initialen(name))}</span>`;
}

export function tagSymbolHtml(farbe: TagFarbe): string {
  return `<span class="${TAG_PUNKT[farbe]}" aria-hidden="true"></span>`;
}

const HAKEN =
  '<svg class="shrink-0 size-4 text-primary" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"/></svg>';
const PFEILE =
  '<div class="absolute top-1/2 inset-e-3 -translate-y-1/2"><svg class="shrink-0 size-3.5 text-muted-foreground-1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/></svg></div>';
const KREUZ =
  '<svg class="shrink-0 size-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

const TOGGLE: Record<FeldGroesseHs, { normal: string; fehler: string }> = {
  standard: {
    normal:
      "hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-3 ps-4 pe-9 flex text-nowrap w-full cursor-pointer bg-layer border border-layer-line text-layer-foreground rounded-lg text-start text-sm hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus",
    fehler:
      "hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-3 ps-4 pe-9 flex text-nowrap w-full cursor-pointer bg-layer border border-red-500 text-layer-foreground rounded-lg text-start text-sm hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus",
  },
  kompakt: {
    normal:
      "hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-2 ps-3 pe-9 flex text-nowrap w-full cursor-pointer bg-layer border border-layer-line text-layer-foreground rounded-lg text-start text-sm hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus",
    fehler:
      "hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-2 ps-3 pe-9 flex text-nowrap w-full cursor-pointer bg-layer border border-red-500 text-layer-foreground rounded-lg text-start text-sm hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus",
  },
  // ruhig (A4′): Rahmen und Grund nur bei Hover und Fokus; der Fokusrahmen (Rand + Ring) ist immer sichtbar.
  ruhig: {
    normal:
      "hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-1.5 ps-2 pe-9 flex text-nowrap w-full cursor-pointer bg-transparent border border-transparent text-layer-foreground rounded-md text-start text-sm hover:border-layer-line hover:bg-layer focus:outline-hidden focus:bg-layer focus:border-primary-focus focus:ring-1 focus:ring-primary-focus",
    fehler:
      "hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-1.5 ps-2 pe-9 flex text-nowrap w-full cursor-pointer bg-layer border border-red-500 text-layer-foreground rounded-md text-start text-sm hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus",
  },
};

const TAGS_HUELLE: Record<"normal" | "fehler", string> = {
  normal:
    "relative ps-0.5 pe-9 min-h-11.5 flex items-center flex-wrap text-nowrap w-full bg-layer border border-layer-line rounded-lg text-start text-sm focus:border-primary-focus focus:ring-primary-focus",
  fehler:
    "relative ps-0.5 pe-9 min-h-11.5 flex items-center flex-wrap text-nowrap w-full bg-layer border border-red-500 rounded-lg text-start text-sm focus:border-red-500 focus:ring-red-500",
};

// ruhig (A4′): Chips-Feld ohne Rahmen im Ruhezustand; Rahmen bei Hover, bei Fokus im Feld (`focus-within`) Rand und Ring.
const TAGS_HUELLE_RUHIG =
  "relative ps-0.5 pe-9 min-h-9 flex items-center flex-wrap text-nowrap w-full bg-transparent border border-transparent rounded-md text-start text-sm hover:border-layer-line hover:bg-layer focus-within:bg-layer focus-within:border-primary-focus focus-within:ring-1 focus-within:ring-primary-focus";

const DROPDOWN =
  "mt-2 z-50 w-full max-h-72 p-1 space-y-0.5 bg-select border border-select-line rounded-lg shadow-xl overflow-hidden overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";
const OPTION_EINZELN =
  "hs-selected:bg-select-item-active py-2 px-3 w-full text-sm text-select-item-foreground cursor-pointer hover:bg-select-item-hover rounded-lg focus:outline-hidden focus:bg-select-item-focus";
const OPTION_MEHRFACH =
  "py-2 px-3 w-full text-sm text-select-item-foreground cursor-pointer hover:bg-select-item-hover rounded-lg focus:outline-hidden focus:bg-select-item-focus hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50";
const GRUPPE = "px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground-1";
const SUCHE =
  "block w-full sm:text-sm bg-layer text-foreground border-layer-line rounded-lg focus:border-primary-focus focus:ring-primary-focus before:absolute before:inset-0 before:z-1 py-1.5 sm:py-2 px-3";
const SUCHE_RAHMEN = "bg-select p-2 -mx-1 sticky top-0";
const KEINE_TREFFER = "px-3 py-2 text-sm text-muted-foreground-1";
const TAG_EINGABE =
  "py-2.5 sm:py-3 px-2 min-w-20 flex-1 text-ellipsis rounded-lg order-1 bg-transparent border-transparent text-foreground placeholder:text-muted-foreground-1 focus:ring-0 sm:text-sm outline-hidden";
// ruhig (A6-Fix): die Eingabe bricht in eine eigene Zeile um, sobald die Chips die Breite füllen; sie ist dort so flach wie das Feld (vorher ~44 px Leerraum je Zeile).
const TAG_EINGABE_RUHIG =
  "py-1 px-2 min-w-20 flex-1 text-ellipsis rounded-lg order-1 bg-transparent border-transparent text-foreground placeholder:text-muted-foreground-1 focus:ring-0 sm:text-sm outline-hidden";

export interface KonfigurationEingabe {
  /** Id des Auslösers (Label `for`); bei Tags die Id des Eingabefeldes. */
  id: string;
  modus: HsModus;
  optionen: HsOption[];
  platzhalter: string;
  mitSuche: boolean;
  suchePlatzhalter?: string;
  keineTreffer?: string;
  /** Zähltext hinter der Zahl bei Mehrfachauswahl („ausgewählt“). */
  zaehlerText?: string;
  groesse: FeldGroesseHs;
  fehlerhaft: boolean;
  /** Ids der Hilfe-/Fehler-Texte (`aria-describedby`). */
  beschreibung?: string;
  /** Erste Option mit leerem Wert („— kein Zugewiesener —“). */
  leerLabel?: string;
}

function optionsVorlage(mitSymbol: boolean, mitBeschreibung: boolean): string {
  const symbol = mitSymbol ? '<div class="me-2" data-icon></div>' : "";
  const beschreibung = mitBeschreibung ? '<div class="text-xs text-muted-foreground-1" data-description></div>' : "";
  return `<div class="flex items-center w-full">${symbol}<div class="min-w-0"><div class="hs-selected:font-semibold text-sm text-foreground" data-title></div>${beschreibung}</div><div class="ms-auto"><span class="hidden hs-selected:block">${HAKEN}</span></div></div>`;
}

/** Baut das Objekt für `data-hs-select` (JSON). */
export function baueAuswahlKonfiguration(e: KonfigurationEingabe): Record<string, unknown> {
  const mitSymbol = e.optionen.some((o) => o.symbolHtml);
  const mitBeschreibung = e.optionen.some((o) => o.beschreibung);
  const beschreibung = e.beschreibung ? ` aria-describedby="${esc(e.beschreibung)}"` : "";
  const ungueltig = e.fehlerhaft ? ' aria-invalid="true"' : "";

  const gemeinsam: Record<string, unknown> = {
    placeholder: e.platzhalter,
    dropdownClasses: DROPDOWN,
    optionClasses: e.modus === "einzeln" ? OPTION_EINZELN : OPTION_MEHRFACH,
    optionTemplate: optionsVorlage(mitSymbol, mitBeschreibung),
    optgroupClasses: GRUPPE,
    extraMarkup: PFEILE,
    ...(e.mitSuche
      ? {
          hasSearch: true,
          searchPlaceholder: e.suchePlatzhalter ?? "Suchen …",
          searchNoResultText: e.keineTreffer ?? "Keine Treffer.",
          searchNoResultClasses: KEINE_TREFFER,
          searchClasses: SUCHE,
          searchWrapperClasses: SUCHE_RAHMEN,
        }
      : {}),
    ...(e.leerLabel ? { optionAllowEmptyOption: true } : {}),
  };

  if (e.modus === "tags") {
    const chip =
      '<div class="flex flex-nowrap items-center min-w-0 max-w-[calc(100%-0.5rem)] relative z-10 bg-layer border border-layer-line rounded-full p-1 m-1"><div class="inline-flex items-center justify-center size-5 me-1.5 shrink-0" data-icon></div><div class="min-w-0 truncate text-sm text-foreground" data-title></div><div class="inline-flex shrink-0 justify-center items-center size-5 ms-2 rounded-full bg-surface text-surface-foreground hover:bg-surface-hover focus:outline-hidden focus:bg-surface-focus text-sm cursor-pointer" data-remove>' +
      KREUZ +
      "</div></div>";
    return {
      ...gemeinsam,
      mode: "tags",
      wrapperClasses: e.fehlerhaft ? TAGS_HUELLE.fehler : e.groesse === "ruhig" ? TAGS_HUELLE_RUHIG : TAGS_HUELLE.normal,
      tagsItemTemplate: chip,
      tagsInputId: e.id,
      tagsInputClasses: e.groesse === "ruhig" ? TAG_EINGABE_RUHIG : TAG_EINGABE,
    };
  }

  const toggleKlassen = e.fehlerhaft ? TOGGLE[e.groesse].fehler : TOGGLE[e.groesse].normal;
  const ausloeserAttribute = `type="button" id="${esc(e.id)}" aria-expanded="false" aria-haspopup="listbox"${beschreibung}${ungueltig}`;
  const toggleTag = mitSymbol && e.modus === "einzeln"
    ? `<button ${ausloeserAttribute}><span class="me-2" data-icon></span><span class="text-foreground" data-title></span></button>`
    : `<button ${ausloeserAttribute}></button>`;
  return {
    ...gemeinsam,
    toggleTag,
    toggleClasses: toggleKlassen,
    ...(e.modus === "mehrfach" ? { toggleCountText: e.zaehlerText ?? "ausgewählt", toggleCountTextMinItems: 1 } : {}),
  };
}
